import dayjs from 'dayjs';
import styled from 'styled-components';

import {
  IconCheckInCircle,
  IconClose,
  IconInfo,
  IconOutlinedCheckInCircle,
  IconWarning,
} from 'assets/icons';
import { IconSeeMore } from 'assets/icons';

import {
  UnfixedTicket,
  FixedTicket,
  ScheduleCancellationData,
  ScheduleCancellationReason,
} from 'types/api';
import ProductStatusBadge from '../components/ProductStatusBadge';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRODUCT_DETAIL_PATH, SALE_LIST_PATH } from 'constants/path';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import Column from 'components/common/Column';
import Text from 'components/common/Text';
import Textarea from 'containers/ProductRegist/components/common/Textarea';
import Row from 'components/common/Row';
import useMutationCancelSchedule from '../graphQL/hooks/useMutationCancelSchedule';
import IconCheck from 'assets/icons/IconCheck';
import { useMutationRequestBookingExcel } from '../graphQL/hooks/useMutationRequestBookingExcel';
import { BookingTermType } from '../graphQL/types';
import ExcelLoading from '../components/ExcelLoading';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';
import ExcelMobileAlert from '../components/ExcelMobileAlert';

const Header = styled.div<{ $isMobile: boolean }>`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;

  z-index: 16;

  padding: 36px 20px 24px;

  background-color: #fff;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }

  .HeaderWrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;

    margin-bottom: 8px;

    > p {
      width: fit-content;
      margin-right: 8px;

      font-weight: 600;
      font-size: 24px;
      line-height: 32px;
      white-space: nowrap;

      color: #1b1c1e;
    }
  }

  .badge-box {
    display: flex;
    flex-direction: row;
    gap: ${({ $isMobile }) => ($isMobile ? '4px' : '8px')};

    width: 100%;
  }

  .suffix-box {
    display: flex;
    align-items: center;
    gap: 24px;

    cursor: pointer;

    svg {
      width: 32px;
      height: 32px;

      @media (max-width: 768px) {
        width: 24px;
        height: 24px;
      }
    }
  }
`;

const OptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  position: relative;

  > ul {
    display: flex;
    flex-direction: column;
    gap: 20px;

    position: absolute;
    top: 32px;
    right: 0;

    width: max-content;
    padding: 20px 16px;
    margin: 0px;

    border-radius: 12px;
    border: 0px;

    background-color: #fff;
    box-shadow: 0px 2px 10px 0px #00000026;

    li {
      list-style: none;

      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: #414b56;

      &.cancel {
        color: #ff4242;
      }
    }
  }
`;

const TimeTextBox = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  > p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    word-break: break-all;
  }

  > .date-text {
    margin-right: 4px;

    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(70, 71, 76, 1);
  }

  > .time-text {
    font-weight: 600;
    font-size: 18px;
    line-height: 26px;

    color: rgba(70, 71, 76, 1);
  }
`;

const ModalHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  padding: 24px 20px 24px;

  > h6 {
    margin: 0;

    font-size: 20px;
    color: rgba(27, 28, 30, 1);
    font-weight: 600;
    line-height: 28px;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  padding: 0px 20px 48px;
`;

const RadioBox = styled.input`
  width: 24px;
  height: 24px;
  appearance: none;
  border: 3px solid #eee;
  border-radius: 50%;
  cursor: pointer;

  &:checked {
    border: 8px solid #1b1c1e;
  }
`;

const ModalFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  position: sticky;
  bottom: 0;
  padding: 16px 20px;
  margin-top: auto;

  width: 100%;
  background-color: inherit;
  border-radius: 0 0 16px 16px;
  border-top: 1px solid #dbdcdf;
`;

const CancelButton = styled.button<{ $isDisabled: boolean }>`
  padding: 0 12px;

  min-width: 104px;
  padding: 0 24px;

  border: none;
  border-radius: 8px;

  font-weight: 600;
  font-size: 16px;
  line-height: 48px;

  cursor: pointer;

  ${({ $isDisabled }) => `
    color:  ${$isDisabled ? 'rgba(219, 220, 223, 1)' : 'rgba(255, 66, 66, 1)'} ;
    background-color: ${
      $isDisabled ? 'rgba(244, 244, 245, 1)' : 'rgba(255, 230, 230, 1)'
    };
  `}
`;

const ConfirmTitle = styled.h6`
  margin: 24px 20px 8px;

  font-size: 20px;
  color: rgba(27, 28, 30, 1);
  font-weight: 600;
  line-height: 28px;
`;

const ConfirmModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  padding: 0px 20px 16px;

  p[data-type='help-text'] {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #70737c;
  }
`;

const ConfirmBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  width: fit-content;

  cursor: pointer;
`;

const ConfirmText = styled.p<{ $isConfirm: boolean }>`
  color: ${({ $isConfirm }) => ($isConfirm ? '#0071E9' : '#DBDCDF')};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`;

const ConfirmModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 16px 20px;

  button[data-type='text'] {
    margin-right: auto;

    border: none;
    outline: none;

    background-color: transparent;

    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #70737c;
  }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  width: calc(100% - 40px);
  padding: 14px 12px;

  margin: 0px auto 24px;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);
  border-radius: 12px;

  p {
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;

    color: rgba(70, 71, 76, 1);

    &[data-type='warning'] {
      margin-top: 4px;

      font-weight: 400;
      font-size: 11px;
      line-height: 14px;

      color: rgba(255, 66, 66, 1);
    }
  }
`;

const CANCEL_REASON_LIST = [
  {
    label: '개인 사정',
    value: ScheduleCancellationReason.PERSONAL,
  },
  {
    label: '기상 악화',
    value: ScheduleCancellationReason.WEATHER,
  },

  {
    label: '재고 부족',
    value: ScheduleCancellationReason.LACK_OF_SUPPLIES,
  },

  {
    label: '기타',
    value: ScheduleCancellationReason.ETC,
  },
];

interface CancelConfirmModalProps {
  isMobile: boolean;
  handleConfirmCancelModal: () => void;
  submitCancelRequest: () => void;
}

const CancelConfirmModal = ({
  isMobile,
  handleConfirmCancelModal,
  submitCancelRequest,
}: CancelConfirmModalProps) => {
  const [isConfirm, setIsConfirm] = useState(false);

  const confirmHandler = () => {
    setIsConfirm(prev => !prev);
  };

  const handleCancelConfirmModal = () => {
    if (isConfirm) {
      submitCancelRequest();
    }
  };

  return (
    <ModalContainer
      isAbled={true}
      isMobile={false}
      isBottomSheet
      onClose={() => {}}
      onClick={() => {}}
      style={{
        gap: 0,
        overflowY: 'hidden',
        ...(!isMobile && { maxWidth: '351px' }),
      }}
    >
      <ConfirmTitle>일정 취소를 진행할까요?</ConfirmTitle>

      <ConfirmModalBody>
        <p data-type="help-text">
          일정 취소 시 예약자는 환불 처리가 진행되며, 취소된 일정은 다시 복구할
          수 없어요.
        </p>

        <ConfirmBox onClick={confirmHandler}>
          {isConfirm ? (
            <IconCheckInCircle color={'#0071E9'} width={20} height={20} />
          ) : (
            <IconOutlinedCheckInCircle width={20} height={20} />
          )}

          <ConfirmText $isConfirm={isConfirm}>네,확인했어요.</ConfirmText>
        </ConfirmBox>
      </ConfirmModalBody>

      <ConfirmModalFooter>
        <button data-type="text" onClick={handleConfirmCancelModal}>
          취소
        </button>
        <CancelButton
          $isDisabled={!isConfirm}
          onClick={handleCancelConfirmModal}
        >
          일정 취소하기
        </CancelButton>
      </ConfirmModalFooter>
    </ModalContainer>
  );
};

interface CancelOrderModalProps {
  isMobile: boolean;
  handleCancelScheduleModal: () => void;
  handleConfirmCancelModal: () => void;
  cancelDataParam: ScheduleCancellationData;
  handleCancelData: (key: string, value: any) => void;
}

const CancelOrderModal = ({
  isMobile,
  handleCancelScheduleModal,
  handleConfirmCancelModal,
  cancelDataParam,
  handleCancelData,
}: CancelOrderModalProps) => {
  return (
    <ModalContainer
      isAbled={true}
      isMobile={isMobile}
      onAcceptText="일정 취소하기"
      isBottomSheet
      onClose={() => {}}
      onClick={() => {}}
      style={{
        gap: 0,
        overflowY: 'hidden',
        ...(!isMobile && { maxWidth: '375px' }),
      }}
    >
      <ModalHeader>
        <h6>일정 취소하기</h6>

        <IconClose
          onClick={handleCancelScheduleModal}
          width={24}
          height={24}
          color={'#70737C'}
        />
      </ModalHeader>

      <ModalBody>
        <Row gap="12px" align="flex-start" justifyContent="flex-start">
          <IconWarning width={22} height={22} />
          <Text size="16px" weight={500} color="#FF4242">
            예약자는 환불 처리가 진행되며, 취소된 일정은 다시 복구할 수 없어요.
          </Text>
        </Row>

        <Column gap="12px" align="flex-start">
          <Text color="#1b1c1e" size="16px">
            일정 취소 사유
          </Text>

          <Row
            gap="16px"
            style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}
          >
            {CANCEL_REASON_LIST.map(({ label, value }) => {
              const isChecked = cancelDataParam.reason === value;

              return (
                <Row
                  gap="8px"
                  onClick={() => handleCancelData('reason', value)}
                >
                  <RadioBox
                    type="radio"
                    checked={isChecked}
                    onChange={() => handleCancelData('reason', value)}
                    style={{
                      margin: 0,
                    }}
                  />
                  <Text color={isChecked && '#1b1c1e'}>{label}</Text>
                </Row>
              );
            })}
          </Row>
        </Column>

        <Column gap="12px" align="flex-start">
          <Text color="#1b1c1e" size="16px">
            상세 사유
          </Text>
          <Textarea
            target="message"
            value={cancelDataParam.message}
            placeholder="자세한 사유를 입력해 주세요"
            onChange={handleCancelData}
            style={{ minHeight: '110px' }}
          />
        </Column>
      </ModalBody>

      <ModalFooter>
        <CancelButton
          onClick={handleConfirmCancelModal}
          $isDisabled={!(cancelDataParam.message && cancelDataParam.reason)}
        >
          일정 취소하기
        </CancelButton>
      </ModalFooter>
    </ModalContainer>
  );
};

interface Props {
  isMobile: boolean;
  startTime: number;
  endTime: number;
  item: FixedTicket | UnfixedTicket;
  handleModal: () => void;
  handleRefetchBookingList: () => void;
  ticketType: 'fixed' | 'unfixed';
  isOverActionTime?: boolean;
}

const BookingDetailModalHeader = ({
  isMobile,
  startTime,
  endTime,
  item,
  handleModal,
  handleRefetchBookingList,
  ticketType,
  isOverActionTime = false,
}: Props) => {
  const navigate = useNavigate();

  const [isMobileExcelAlertOpen, setIsMobileExcelAlertOpen] = useState(false);
  const hostId = useUserInformation(state => state.userInfo.me.host.id);

  const { excelDownloadLoading, handleRequestBookingExcel } =
    useMutationRequestBookingExcel();

  const [cancelDataParam, setCancelDataParam] =
    useState<ScheduleCancellationData>({
      reason: null,
      message: '',
    });

  const [isOptionBoxOpen, setIsOptionBoxOpen] = useState(false);
  const [isScheduleCancelModalOpen, setIsScheduleCancelModalOpen] =
    useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);

  const isSameDay =
    dayjs(startTime).format('YYYY-MM-DD') ===
    dayjs(endTime).format('YYYY-MM-DD');

  const { handleCancelSchedule } = useMutationCancelSchedule();

  const isFixedTicket = 'minimumQuota' in item;

  const handleMobileExcelDownload = () => {
    setIsMobileExcelAlertOpen(prev => !prev);
  };

  /* 옵션 박스 열기 */
  const handleModalOptionBox = () => {
    setIsOptionBoxOpen(prev => !prev);
  };

  /* 일정 취소 모달 열기 */
  const handleModalScheduleCancel = () => {
    setIsScheduleCancelModalOpen(prev => !prev);
  };

  /* 판매내역에서 보기 */
  const goToSalesPage = () => {
    localStorage.setItem('scheduleIds', item.id);

    handleModalOptionBox();

    return navigate(SALE_LIST_PATH);
  };

  /* 일정 수정하기 */
  const editSchedule = () => {
    handleModalOptionBox();

    let productId: string = '';

    if (isFixedTicket) {
      /* 지정형일 경우 */
      productId = (item as FixedTicket).product.id;
    } else {
      productId = (item as UnfixedTicket).bookingItem.booking.info.product.id;
    }

    return navigate(`${PRODUCT_DETAIL_PATH}/${productId}`);
  };

  /* 엑셀로 내려받기 */
  const handleGetBookingExcel = () => {
    if (isMobile) {
      handleMobileExcelDownload();
      return;
    }

    const fixedFilter = {
      scheduleId: item.id,
    };
    const unfixedFilter = {
      productIds: [
        (item as UnfixedTicket)?.bookingItem?.booking?.info?.product?.id || '',
      ],
      // unscheduled: true,
    };

    handleRequestBookingExcel({
      hostId,
      termTypes: ['STARTING'],
      term: {
        startedAt: startTime,
        endedAt: startTime,
      },
      ...(ticketType === 'fixed' ? fixedFilter : unfixedFilter),
    });
  };

  /* 일정 취소하기 */
  const cancelSchedule = () => {
    handleModalScheduleCancel();
    setTimeout(() => {
      handleModalOptionBox();
    }, 0);
  };

  /* 일정 취소 데이터 변경 */
  const handleCancelDataChange = (key: string, value: any) => {
    setCancelDataParam(pre => ({ ...pre, [key]: value }));
  };

  /* 일정 취소 확인 모달 열기 */
  const handleConfirmCancelModal = () => {
    setIsConfirmCancelModalOpen(prev => !prev);
  };

  const refetchBookingList = () => {
    if (isMobile) {
      /* 모바일 경우, 이때가지 불러왔던 데이터를 filter를 통해서 재 호출.  */
    }

    return handleRefetchBookingList();
  };

  /* 일정 취소 요청 API 호출 */
  const submitCancelRequest = async () => {
    handleConfirmCancelModal(); // 컨펌 모달 닫기
    handleModalScheduleCancel(); // 일정 취소 모달 닫기
    handleModal(); // 디테일 모달 닫기

    handleCancelSchedule(item.id, cancelDataParam, refetchBookingList);
  };

  const isScheduled = isOverActionTime ? true : !isFixedTicket ? true : false; // FIXME: 지정형일때 false대신 값 계산해야함

  const isExactlySameDay =
    startTime ===
    endTime; /* 대부분 조율형인경우. 예약확정시 duration을 0으로 보내기때문에 */

  const getTimeDiff = () => {
    if (isSameDay) {
      const totalMinutes = dayjs(endTime).diff(dayjs(startTime), 'minute');
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (minutes === 0) {
        return `(${hours}시간)`;
      }

      return `(${hours}시간 ${minutes}분)`;
    }

    return `${dayjs(endTime).diff(dayjs(startTime), 'day') + 1}일`;
  };

  return (
    <>
      <Header $isMobile={isMobile}>
        <div className="HeaderWrapper">
          {isScheduled && <p>확정된 일정</p>}
          <div className="badge-box">
            <ProductStatusBadge item={item} hideCount={true} />
          </div>

          <div className="suffix-box">
            <OptionBox onClick={handleModalOptionBox}>
              <IconSeeMore color={'rgba(112, 115, 124, 1)'} />

              {isOptionBoxOpen && (
                <ul>
                  {ticketType === 'fixed' && (
                    <li onClick={goToSalesPage}>판매내역에서 보기</li>
                  )}
                  <li onClick={editSchedule}>상품 수정하기</li>
                  <li onClick={handleGetBookingExcel}>엑셀로 내려받기</li>
                  {ticketType === 'fixed' && (
                    <li className="cancel" onClick={cancelSchedule}>
                      일정 취소하기
                    </li>
                  )}
                </ul>
              )}
            </OptionBox>

            <IconClose onClick={handleModal} color={'rgba(112, 115, 124, 1)'} />
          </div>
        </div>

        <TimeTextBox>
          <p className="date-text">
            {dayjs(startTime).format('M월 DD일(ddd)')}
          </p>
          <p className="time-text">{dayjs(startTime).format('HH:mm')} ~ </p>
          &nbsp;
          {isExactlySameDay ? (
            ''
          ) : (
            <>
              {!isSameDay && (
                <p className="date-text">
                  {dayjs(endTime).format('M월 DD일(ddd)')}
                </p>
              )}
              <p className="time-text">{dayjs(endTime).format('HH:mm')}</p>
              &nbsp;
              <p className="time-text">{getTimeDiff()}</p>
            </>
          )}
        </TimeTextBox>
      </Header>

      {isScheduled && (
        <InfoBox>
          <IconInfo />
          <p>
            {dayjs(startTime)
              .subtract(1, 'hour')
              .format('YY년 M월 D일 ddd H:mm')}{' '}
            이후에 참석여부 확인이 가능합니다.
          </p>
        </InfoBox>
      )}

      {isScheduleCancelModalOpen && (
        <CancelOrderModal
          isMobile={isMobile}
          handleCancelScheduleModal={handleModalScheduleCancel}
          handleConfirmCancelModal={handleConfirmCancelModal}
          cancelDataParam={cancelDataParam}
          handleCancelData={handleCancelDataChange}
        />
      )}

      {isConfirmCancelModalOpen && (
        <CancelConfirmModal
          isMobile={isMobile}
          handleConfirmCancelModal={handleConfirmCancelModal}
          submitCancelRequest={submitCancelRequest}
        />
      )}

      {excelDownloadLoading && <ExcelLoading />}
      {isMobile && isMobileExcelAlertOpen && (
        <ExcelMobileAlert handleModal={handleMobileExcelDownload} />
      )}
    </>
  );
};

export default BookingDetailModalHeader;
